/* Cliente REST de releases do app (ADR-0659). Staff lista/registra/promove/tira-do-ar via
   `/v1/admin/releases`. */
import {
  availableVersionsDto,
  type BucketList,
  type BulkReleaseActionResponse,
  bucketListDto,
  bulkReleaseActionResponseDto,
  type DeleteReleaseResult,
  type DiscoverRelease,
  deleteReleaseResultDto,
  discoverReleaseDto,
  type PurgeReleaseVersionResult,
  purgeReleaseVersionResultDto,
  type RegisterDiscoveredBody,
  type RegisterDiscoveredResult,
  type ReleaseBulkAction,
  type ReleaseChannel,
  type ReleasePlatform,
  type ReleaseStatus,
  type ReleasesPage,
  registerDiscoveredResultDto,
  releasesPageDto,
  type UpdateReleaseVersionBody,
} from '@planuze/admin-contracts';
import { ApiError, buildQuery, parseJson, readErrorCode, request } from './http.js';

async function releaseError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  return new ApiError(res.status, 'server_error', code || 'request failed');
}

/* Filtros server-side da lista de releases (aba "Releases"). `platforms` é o multi-select do UI,
   enviado como CSV (`platform=mac,win`). Todos são server-side pois a lista é paginada. */
export interface ReleasesQuery {
  readonly channel?: ReleaseChannel | undefined;
  readonly status?: ReleaseStatus | undefined;
  readonly mandatory?: boolean | undefined;
  readonly platforms?: readonly ReleasePlatform[] | undefined;
  readonly search?: string | undefined;
  readonly cursor?: string | undefined;
}

/** GET /v1/admin/releases — página keyset por cursor (filtros server-side + cursor). */
export async function listReleases(query: ReleasesQuery = {}): Promise<ReleasesPage> {
  const res = await request(
    `/v1/admin/releases${buildQuery({
      channel: query.channel,
      status: query.status,
      mandatory: query.mandatory === undefined ? undefined : String(query.mandatory),
      platform:
        query.platforms !== undefined && query.platforms.length > 0
          ? query.platforms.join(',')
          : undefined,
      search: query.search,
      cursor: query.cursor,
    })}`,
  );
  if (!res.ok) throw await releaseError(res);
  return parseJson(res, releasesPageDto);
}

export async function listBucket(): Promise<BucketList['objects']> {
  const res = await request('/v1/admin/releases/bucket');
  if (!res.ok) throw await releaseError(res);
  return (await parseJson(res, bucketListDto)).objects;
}

/* Versões presentes no bucket p/ o canal (o admin escolhe em vez de digitar) — ADR-0670. */
export async function listAvailableVersions(channel: ReleaseChannel): Promise<string[]> {
  const res = await request(`/v1/admin/releases/available?channel=${channel}`);
  if (!res.ok) throw await releaseError(res);
  return (await parseJson(res, availableVersionsDto)).versions;
}

/* Descobre os artefatos de {canal, versão} lendo os manifestos do build no bucket (auto-preenche
   sha512/tamanho). O `error.message` carrega o código (version_mismatch/no_artifacts/…). */
export async function discoverRelease(
  channel: ReleaseChannel,
  version: string,
): Promise<DiscoverRelease> {
  const res = await request(`/v1/admin/releases/discover?channel=${channel}&version=${version}`);
  if (!res.ok) throw await releaseError(res);
  return parseJson(res, discoverReleaseDto);
}

export async function registerDiscovered(
  body: RegisterDiscoveredBody,
): Promise<RegisterDiscoveredResult> {
  const res = await request('/v1/admin/releases/register-discovered', { method: 'POST', body });
  if (!res.ok) throw await releaseError(res);
  return parseJson(res, registerDiscoveredResultDto);
}

export async function promoteRelease(id: string, mandatory: boolean): Promise<void> {
  const res = await request(`/v1/admin/releases/${id}/promote`, {
    method: 'POST',
    body: { mandatory },
  });
  if (!res.ok) throw await releaseError(res);
}

export async function takedownRelease(id: string): Promise<void> {
  const res = await request(`/v1/admin/releases/${id}/takedown`, { method: 'POST' });
  if (!res.ok) throw await releaseError(res);
}

/* Remove a linha do índice e suas métricas; R2/GitHub permanecem imutáveis. O service recusa (409
   `release_is_promoted`) quando a release está no ar; o chamador confirma em Dialog antes. */
export async function deleteRelease(id: string): Promise<DeleteReleaseResult> {
  const res = await request(`/v1/admin/releases/${id}`, { method: 'DELETE' });
  if (!res.ok) throw await releaseError(res);
  return parseJson(res, deleteReleaseResultDto);
}

/** Remove o prefixo físico inteiro; registros, histórico e métricas ficam preservados no tombstone. */
export async function purgeReleaseVersion(
  channel: ReleaseChannel,
  version: string,
  confirmation: string,
  reason: string,
): Promise<PurgeReleaseVersionResult> {
  const encodedChannel = encodeURIComponent(channel);
  const encodedVersion = encodeURIComponent(version);
  const res = await request(`/v1/admin/release-versions/${encodedChannel}/${encodedVersion}/r2`, {
    method: 'DELETE',
    body: { confirmation, reason },
  });
  if (!res.ok) throw await releaseError(res);
  return parseJson(res, purgeReleaseVersionResultDto);
}

/* Ação em lote. Responde 200 com resultado POR ITEM mesmo com falhas parciais — quem chama lê
   `results` para dizer quantos passaram, em vez de tratar tudo-ou-nada. */
export async function bulkReleaseAction(
  ids: readonly string[],
  action: ReleaseBulkAction,
): Promise<BulkReleaseActionResponse> {
  const res = await request('/v1/admin/releases/bulk', {
    method: 'POST',
    body: { ids: [...ids], action },
  });
  if (!res.ok) throw await releaseError(res);
  return parseJson(res, bulkReleaseActionResponseDto);
}

async function updateReleaseVersion(
  channel: ReleaseChannel,
  version: string,
  body: UpdateReleaseVersionBody,
): Promise<void> {
  const res = await request(
    `/v1/admin/release-versions/${encodeURIComponent(channel)}/${encodeURIComponent(version)}`,
    {
      method: 'PATCH',
      body,
    },
  );
  if (!res.ok) throw await releaseError(res);
}

export async function updateReleaseNotes(
  channel: ReleaseChannel,
  version: string,
  releaseNotes: string | null,
): Promise<void> {
  await updateReleaseVersion(channel, version, { releaseNotes });
}

export async function updateReleaseMandatory(
  channel: ReleaseChannel,
  version: string,
  mandatory: boolean,
): Promise<void> {
  await updateReleaseVersion(channel, version, { mandatory });
}
